/**
 * Bug Report Modal
 *
 * Responsive modal for submitting bug reports with:
 * - Left panel: Screenshot capture/preview
 * - Right panel: Form fields (title, severity, description, steps)
 * - Bottom: Auto-collected context (collapsible)
 * - Submit/Cancel actions
 */

import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Info, Building2 } from 'lucide-react'
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalFooter,
} from '@/components/ui/responsive-modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useBugReport } from '../hooks/useBugReport'
import { useConsoleCapture } from '../hooks/useConsoleCapture'
import { ScreenshotCapture } from './ScreenshotCapture'
import { FileAttachments } from './FileAttachments'
import { VoiceRecorder } from './VoiceRecorder'
import type { BugSeverity } from '../types/feedback.types'
import type { Deal } from '@/features/dashboard/types/dashboard.types'

interface BugReportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BugReportModal({ open, onOpenChange }: BugReportModalProps) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const { getErrors } = useConsoleCapture()

  // Extract deal ID and name from URL and React Query cache
  const dealId = location.pathname.match(/\/deals\/([^/]+)/)?.[1]
  // Individual deals are cached with ['deal', dealId] key
  const deal = dealId ? queryClient.getQueryData(['deal', dealId]) as Deal | undefined : undefined
  const dealName = deal?.company_name

  // Capture diligence data from React Query cache for debugging
  // This snapshot is preserved even if the deal is later deleted
  const getDiligenceSnapshot = (): string | undefined => {
    if (!dealId) return undefined

    const diligenceSummary = queryClient.getQueryData(['deal-diligence', dealId])
    const diligenceFindings = queryClient.getQueryData(['deal-diligence-findings', dealId])

    // Only create snapshot if we have some diligence data
    if (!diligenceSummary && !diligenceFindings) return undefined

    return JSON.stringify({
      deal_id: dealId,
      deal_name: dealName,
      captured_at: new Date().toISOString(),
      summary: diligenceSummary,
      findings: diligenceFindings,
    })
  }

  // Build initial description with deal context
  const getInitialDescription = () => {
    if (dealId && dealName) {
      return `Deal: ${dealName}\n\nWhat is the problem you're seeing?\n`
    }
    return ''
  }

  // Form state
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState<BugSeverity>('minor')
  const [description, setDescription] = useState(getInitialDescription)
  const [stepsToReproduce, setStepsToReproduce] = useState('')
  const [showContext, setShowContext] = useState(false)

  // Reset form function
  const resetForm = () => {
    setTitle('')
    setSeverity('minor')
    setDescription(getInitialDescription())
    setStepsToReproduce('')
  }

  // Bug report hook
  const {
    screenshot,
    isCapturing,
    captureScreenshot,
    handlePaste,
    clearScreenshot,
    attachments,
    setFiles,
    clearAttachments,
    submitMutation,
  } = useBugReport({
    onSuccess: () => {
      // Reset form
      resetForm()
      onOpenChange(false)
    },
    getConsoleErrors: getErrors,
  })

  // Auto-collect browser context
  const pageUrl = window.location.href
  const userAgent = navigator.userAgent
  const viewportSize = `${window.innerWidth}x${window.innerHeight}`

  // Get console errors for display
  const consoleErrors = getErrors()

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!title.trim()) {
      return
    }

    submitMutation.mutate({
      title: title.trim(),
      severity,
      page_url: pageUrl,
      user_agent: userAgent,
      viewport_size: viewportSize,
      description: description.trim() || undefined,
      steps_to_reproduce: stepsToReproduce.trim() || undefined,
      deal_id: dealId || undefined,
      deal_name: dealName || undefined,
      deal_diligence_snapshot: getDiligenceSnapshot(),
    })
  }

  // Handle modal close with cleanup
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm()
      clearScreenshot()
      clearAttachments()
    }
    onOpenChange(newOpen)
  }

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
      <ResponsiveModalContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>Report a Bug</ResponsiveModalTitle>
        </ResponsiveModalHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Deal Context Banner */}
          {dealId && dealName && (
            <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <Building2 className="h-5 w-5 text-primary flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Reporting issue with: <span className="font-semibold">{dealName}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Deal ID: {dealId}
                </p>
              </div>
            </div>
          )}

          {/* Main content - Two column layout on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Panel - Screenshot */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Screenshot (Optional)
                </Label>
                <ScreenshotCapture
                  screenshot={screenshot}
                  isCapturing={isCapturing}
                  onCapture={captureScreenshot}
                  onPaste={handlePaste}
                  onClear={clearScreenshot}
                />
              </div>
            </div>

            {/* Right Panel - Form */}
            <div className="space-y-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  placeholder="Brief description of the bug"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              {/* Severity */}
              <div className="space-y-2">
                <Label htmlFor="severity">
                  Severity <span className="text-destructive">*</span>
                </Label>
                <Select value={severity} onValueChange={(value) => setSeverity(value as BugSeverity)}>
                  <SelectTrigger id="severity">
                    <SelectValue placeholder="Select severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">
                      Critical - App is unusable
                    </SelectItem>
                    <SelectItem value="major">
                      Major - Important feature broken
                    </SelectItem>
                    <SelectItem value="minor">Minor - Small issue</SelectItem>
                    <SelectItem value="cosmetic">
                      Cosmetic - Visual only
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe what you expected vs what happened."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
                <div className="flex items-center gap-2">
                  <VoiceRecorder
                    onTranscription={(text) => {
                      setDescription((prev) => prev.trim() ? `${prev}\n\n${text}` : text)
                    }}
                    disabled={submitMutation.isPending}
                  />
                  <span className="text-xs text-muted-foreground">
                    or type above
                  </span>
                </div>
              </div>

              {/* Steps to Reproduce */}
              <div className="space-y-2">
                <Label htmlFor="steps">Steps to Reproduce</Label>
                <Textarea
                  id="steps"
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* File Attachments */}
          <div className="space-y-2">
            <Label>Additional Attachments (Optional)</Label>
            <FileAttachments
              files={attachments}
              onFilesChange={setFiles}
            />
          </div>

          {/* Auto-collected Context - Collapsible */}
          <Collapsible open={showContext} onOpenChange={setShowContext}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-between"
              >
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4" />
                  Auto-collected context ({consoleErrors.length} console errors)
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${showContext ? 'rotate-180' : ''}`}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-2">
              <div className="rounded-md bg-muted p-3 text-xs font-mono space-y-1">
                <div>
                  <span className="text-muted-foreground">Page URL:</span>{' '}
                  <span className="break-all">{pageUrl}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Viewport:</span> {viewportSize}
                </div>
                {dealId && (
                  <div>
                    <span className="text-muted-foreground">Deal ID:</span> {dealId}
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">User Agent:</span>{' '}
                  <span className="break-all">{userAgent}</span>
                </div>
                {consoleErrors.length > 0 && (
                  <div className="mt-2">
                    <div className="text-muted-foreground mb-1">
                      Console Errors (last {consoleErrors.length}):
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {consoleErrors.map((error, i) => (
                        <div
                          key={i}
                          className="text-destructive bg-destructive/10 p-1 rounded"
                        >
                          [{error.type}] {error.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Footer Actions */}
          <ResponsiveModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || submitMutation.isPending}
            >
              {submitMutation.isPending ? 'Submitting...' : 'Submit Bug Report'}
            </Button>
          </ResponsiveModalFooter>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
